import { connect, useDispatch } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Dispatch } from 'redux';
import { Form, FormRenderProps } from 'react-final-form';
import { FormApi } from 'final-form';
import { path } from 'ramda';

import { ISelectOption, SelectInput } from 'UIPackage/MainModule/components/SelectInput';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getErknmKnmPrescribeDataForCancelSign,
  getKnmErknmPositionsSelectOptions,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IDictionaryParticipantPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryParticipantPositionSelectOption';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { HadToBeHowInSignMessage } from 'KnmErknmPackage/MainModule/components/HadToBeHowInSignMessage';
import { getCertificates, ICertificateItem } from 'KnmPackage/MainModule/common/cryptoHelpers';
import { getStorageItem, setStorageItem } from 'CorePackage/MainModule/common/localStore';
import { StorageNamesEnum } from 'CorePackage/MainModule/enums/StorageNamesEnum';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { coreSetLoading } from 'CorePackage/MainModule/actions/coreActions';
import { ISignSubjectInfo } from 'APIPackage/KnmModule/interfaces/ISignSubjectInfo';
import { knmSetSelectedPrescribeCancelData } from 'KnmPackage/MainModule/actions/knmActions';
import { IPrescribeCancelData } from 'KnmPackage/MainModule/types/IKnmState';
import { getDecisionParticipantPositionsSelectOptions } from 'DecisionPackage/MainModule/selectors/decisionSelectors';

import styles from './ErknmSignAfterCancelModal.module.scss';

interface IErknmSignAfterCancelModalProps {
  visible: boolean;
  onClose: () => void;
  onSign: (certificate: string, prescriptionGuid?: string) => void;
  isPm: boolean;
  isDecision: boolean;
  isKnm: boolean;
}

interface IErknmSignAfterCancelModalStoreProps {
  decisionPositionsSelectOptions:
    | IDictionaryPositionSelectOption[]
    | IDictionaryParticipantPositionSelectOption[]
    | undefined;
  positionsSelectOptions: IDictionaryPositionSelectOption[] | IDictionaryParticipantPositionSelectOption[] | undefined;
  prescribeData?: IPrescribeCancelData;
}

interface IForm {
  fioSigner: string;
  signerPosition: ISelectOption<string>;
}

const ErknmSignAfterCancelModalWithoutStore = ({
  visible,
  onClose,
  onSign,
  isDecision = false,
  isKnm = false,
  isPm = true,
  positionsSelectOptions,
  prescribeData,
  decisionPositionsSelectOptions,
}: IErknmSignAfterCancelModalProps & IErknmSignAfterCancelModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const formRefInner = useRef<FormApi<IForm, Partial<IForm>>>();
  const [certificates, setCertificates] = useState<ISelectOption<string>[]>([]);
  const [certificate, setSelectedCertificate] = useState<ISelectOption<string> | undefined>(undefined);
  const [certFio, setCertFio] = useState<string | undefined>(undefined);

  /**
   * Парсинг информации из сертификата
   */
  const parseSubjectName = useCallback((str: string) => {
    const regex = /[A-Z]*?=/gm;

    let m;

    const objs: { key: string; value: string; position: number }[] = [];

    // eslint-disable-next-line no-cond-assign
    while ((m = regex.exec(str)) !== null) {
      if (m.index === regex.lastIndex) {
        // eslint-disable-next-line no-plusplus
        regex.lastIndex++;
      }

      m.forEach(key => {
        objs.push({ key, value: '', position: str.indexOf(key) });
      });
    }

    const removeQuotes = (value: string) => {
      if (value[0] === '"' && value[value.length - 1] === '"') {
        return value.substring(1, value.length - 1);
      }

      return value;
    };

    return objs.map((obj, i) => {
      const newObj = obj;

      if (i + 1 < objs.length) {
        newObj.value = str.substring(obj.position + obj.key.length, objs[i + 1].position);

        if (newObj.value[newObj.value.length - 1] === ' ' && newObj.value[newObj.value.length - 2] === ',') {
          newObj.value = newObj.value.substring(0, newObj.value.length - 2);
        }

        newObj.value = removeQuotes(newObj.value);
        newObj.key = newObj.key.replace('=', '');

        return newObj;
      }
      newObj.value = str.substring(obj.position + obj.key.length);
      newObj.value = removeQuotes(newObj.value);
      newObj.key = newObj.key.replace('=', '');

      return newObj;
    });
  }, []);

  /**
   * Функция извлечения из сертификата ФИО и организации
   */
  const getSubjectInfo = useCallback((subjectName: string) => {
    const array = parseSubjectName(subjectName);

    const org = array.find(i => i.key === 'O')?.value;
    const fullname = array.find(i => i.key === 'CN')?.value;
    const surename = array.find(i => i.key === 'SN')?.value;
    const givenname = array.find(i => i.key === 'G')?.value;

    return { fullname, org, surename, givenname } as ISignSubjectInfo;
  }, []);

  const getCertFio = useCallback(async () => {
    const subjectName = (await path(['SubjectName'], certificate?.value)) as string;
    const subjectInfo = getSubjectInfo(subjectName);

    return subjectInfo.fullname;
  }, [certificate]);

  useEffect(() => {
    if (certificate) {
      getCertFio().then(fio => setCertFio(fio));
    } else {
      setCertFio(undefined);
    }
  }, [certificate, getCertFio]);

  const validateForm = useCallback(
    (values: IForm) => {
      return {
        fioSigner: composeValidators(makeRequired('Поле не может быть пустым'), () => {
          return certFio && values.fioSigner !== certFio
            ? 'ФИО не совпадает со сведениями из выбранной ЭЦП'
            : undefined;
        })(values.fioSigner),
        signerPosition: makeRequired('Поле не может быть пустым')(values.signerPosition),
      };
    },
    [certFio],
  );

  const handleClose = () => {
    dispatch(coreSetLoading(false));
    onClose();
    formRefInner?.current?.reset();
  };

  const fioSignerFieldName = 'fioSigner';
  const titleSignerFieldName = 'signerPosition';

  useEffect(() => {
    if (visible) {
      loadCerts(handleClose);
    }
  }, [handleClose, visible]);

  const loadCerts = async (handleCloseFoo: () => void) => {
    try {
      await getCertificates().then((result: ICertificateItem[]) => {
        if (result) {
          const certs: ISelectOption<string>[] = result.map(item => {
            return { id: item.serialNumber, label: item.info, value: item.value };
          });

          setCertificates(certs);
          if (certs.length === 1) {
            setSelectedCertificate(certs[0]);
          } else if (certs.length > 1) {
            const certSerialNumber = getStorageItem<string | number>(StorageNamesEnum.CERT_SERIAL_NUMBER);

            if (certSerialNumber) {
              const cert = certs.find(item => item.id === certSerialNumber);

              if (cert) {
                setSelectedCertificate(cert);
              }
            }
          }
        } else {
          handleCloseFoo();
        }
      });
    } catch (err) {
      console.error('error from loadCerts', err);
    }
  };

  const handleSelectChange = useCallback((value: ISelectOption<string>) => {
    setStorageItem(StorageNamesEnum.CERT_SERIAL_NUMBER, value.id);
    setSelectedCertificate(value);
  }, []);

  const signCancel = useCallback(() => {
    if (certificate?.value && prescribeData) {
      onSign(certificate.value, prescribeData.prescriptionGuid);

      const newData: IPrescribeCancelData = {
        ...prescribeData,
        fioSigner: formRefInner.current?.getState().values.fioSigner,
        signerPosition: formRefInner.current?.getState().values.signerPosition,
      };

      dispatch(knmSetSelectedPrescribeCancelData(newData));
    }
  }, [certificate?.value, onSign, prescribeData]);

  const renderContentForm = useCallback(
    ({ handleSubmit, form }: FormRenderProps<IForm>) => {
      formRefInner.current = form;

      return (
        <>
          <ModalBody size="content">
            <div className={styles.InputWrapper}>
              <SelectInput
                id="certs"
                name="certs"
                className={styles.SelectInput}
                isClearable={false}
                options={certificates}
                onChange={handleSelectChange}
                value={certificate}
              />
            </div>
            <FieldBlock>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: 'ФИО',
                  field: fioSignerFieldName,
                  isDecision,
                  isKnm,
                  isPm,
                })}
                color="black"
                className={styles.FieldBlock}
                placementLabel="left"
                margin="bottom"
              >
                <TextInputField name={fioSignerFieldName} disabled={false} />
              </FieldBlock>
              <FieldBlock
                type="grow-label"
                color="black"
                className={styles.FieldBlock}
                placementLabel="left"
                margin="bottom"
              >
                <HadToBeHowInSignMessage message={'ФИО должно совпадать со сведениями из ЭЦП'} />
              </FieldBlock>
              <FieldBlock
                label={getTitleFieldWithRedStar({
                  label: 'Должность',
                  field: titleSignerFieldName,
                  isDecision,
                  isKnm,
                  isPm,
                })}
                color="black"
                className={styles.FieldBlock}
                placementLabel="left"
                margin="bottom"
              >
                <SelectInputField
                  name={titleSignerFieldName}
                  placeholder="Выберите должность"
                  options={
                    !isDecision
                      ? positionsSelectOptions || decisionPositionsSelectOptions
                      : decisionPositionsSelectOptions || positionsSelectOptions
                  }
                  isSearchable
                  disabled={false}
                  size="medium"
                />
              </FieldBlock>
              <FieldBlock
                type="grow-label"
                color="black"
                className={styles.FieldBlock}
                placementLabel="left"
                margin="bottom"
              >
                <HadToBeHowInSignMessage message={'Должность лица, уполномоченного на принятие решения (из ЕРВК)'} />
              </FieldBlock>
            </FieldBlock>
          </ModalBody>
          <ModalActions>
            <Button text="Отменить" size="medium" type="button" onClick={handleClose} />
            <Button
              text="Подписать"
              color="primary"
              size="medium"
              type="button"
              onClick={handleSubmit}
              disabled={!certificate}
            />
          </ModalActions>
        </>
      );
    },
    [handleClose],
  );

  return (
    <Modal visible={visible} onClose={handleClose}>
      <ModalHeader title="Подпиcание отмены предписания" />
      <Form<IForm> render={renderContentForm} onSubmit={signCancel} validate={validateForm} />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IErknmSignAfterCancelModalStoreProps => ({
  positionsSelectOptions: getKnmErknmPositionsSelectOptions(state),
  decisionPositionsSelectOptions: getDecisionParticipantPositionsSelectOptions(state),
  prescribeData: getErknmKnmPrescribeDataForCancelSign(state),
});

export const ErknmSignAfterCancelModal = connect(mapStateToProps)(ErknmSignAfterCancelModalWithoutStore);
